import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';

export default function Segmentation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const handleScrollToForm = () => {
    const formSection = document.getElementById('registro-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <section className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <motion.div className="text-center mb-10 md:mb-14" initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#1A2B3C]/8 border border-[#1A2B3C]/15 rounded-full text-[#1A2B3C]/70 text-xs font-bold tracking-wider uppercase mb-4">
            <i className="ri-group-line"></i>
            Segmentación estratégica
          </span>
          <h2 className="text-2xl md:text-4xl font-black text-[#1A2B3C] leading-tight mb-4 tracking-tight">
            Un programa diseñado para cada conductor
          </h2>
          <p className="text-[#1A2B3C]/55 text-sm md:text-base max-w-xl mx-auto">
            Tanto si conduces taxi como vehículo particular, el sistema tiene una ruta técnica específica para ti.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 mb-10">
          {/* Column A — Taxis */}
          <motion.div className="relative overflow-hidden bg-[#1A2B3C] rounded-2xl" initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="relative h-48 overflow-hidden">
              <img src="https://public.readdy.ai/ai/img_res/f9888728de91e5045dc45b07f2d7ce8e.jpg" alt="Servicio público diagnóstico en calle Bogotá" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B3C] via-[#1A2B3C]/30 to-transparent"></div>
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 rounded-full">
                <i className="ri-taxi-line text-amber-400 text-sm"></i>
                <span className="text-amber-400 text-xs font-black tracking-wider uppercase">Servicio Público</span>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="text-white font-black text-xl md:text-2xl mb-2">Taxis</h3>
              <p className="text-white/50 text-sm mb-6 leading-relaxed">Sabemos que tu tiempo es dinero. El diagnóstico llega a ti en campaña presencial, sin interrumpir tu jornada laboral.</p>
              <div className="space-y-4 mb-6">
                {[
                  { icon: 'ri-road-map-line', title: 'Revisión Preventiva Operativa', desc: 'Se realiza en el CDA. Redimible para tu TECNOMECÁNICA (aún no vencida).', tag: '$25.000 redimibles para RTM', tagColor: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
                  { icon: 'ri-user-voice-line', title: 'Atención directa', desc: 'Comunicación directa con el especialista. Sin intermediarios.', tag: null, tagColor: '' },
                  { icon: 'ri-shield-check-line', title: 'Revisiones Preventivas Gratuitas', desc: 'De por vida post-RTM para conductores registrados.', tag: 'Post-RTM', tagColor: 'text-[#28A745] bg-[#28A745]/10 border-[#28A745]/20' },
                  { icon: 'ri-coupon-3-line', title: 'Bono de llantas', desc: 'Bono de llantas redimible para un servicio de TECNOMECÁNICA.', tag: '$15.000 – $20.000 c/u', tagColor: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-500/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${feature.icon} text-amber-400 text-base`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <p className="text-white font-semibold text-sm">{feature.title}</p>
                        {feature.tag && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${feature.tagColor}`}>{feature.tag}</span>}
                      </div>
                      <p className="text-white/40 text-xs">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={handleScrollToForm} className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-taxi-line text-base"></i>
                Registrar mi taxi
              </button>
            </div>
          </motion.div>

          {/* Column B — Particulares */}
          <motion.div className="relative overflow-hidden bg-white border border-[#E1E1E1] rounded-2xl" initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="relative h-48 overflow-hidden">
              <img src="https://readdy.ai/api/search-image?query=modern%20CDA%20vehicle%20inspection%20center%20Colombia%20Bogota%20professional%20automotive%20diagnostic%20equipment%20clean%20organized%20facility%20technician%20inspecting%20private%20car%20safety%20check%20certification%20center%20professional%20photography&width=600&height=300&seq=particular-cda-seg-v2&orientation=landscape" alt="Revisión de seguridad vehículo particular CDA Bogotá" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent"></div>
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#28A745]/15 border border-[#28A745]/40 rounded-full">
                <i className="ri-car-line text-[#28A745] text-sm"></i>
                <span className="text-[#28A745] text-xs font-black tracking-wider uppercase">Vehículos Particulares</span>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <h3 className="text-[#1A2B3C] font-black text-xl md:text-2xl mb-2">Particulares</h3>
              <p className="text-[#1A2B3C]/55 text-sm mb-6 leading-relaxed">Revisión técnica completa en CDA autorizado. Seguridad certificada con valor 100% redimible en servicios.</p>
              <div className="space-y-4 mb-6">
                {[
                  { icon: 'ri-shield-star-line', title: 'Seguro Técnico de Seguridad', desc: 'Se realiza dentro de las instalaciones del CDA aliado. Redimible para tu TECNOMECÁNICA.', tag: '$50.000 redimibles para RTM', tagColor: 'text-[#28A745] bg-[#28A745]/10 border-[#28A745]/20' },
                  { icon: 'ri-building-4-line', title: 'Diagnóstico en CDA aliado', desc: 'Centro de Diagnóstico Automotor verificado. Evaluación completa en instalaciones oficiales.', tag: 'CDA autorizado', tagColor: 'text-[#1A2B3C]/70 bg-[#1A2B3C]/8 border-[#1A2B3C]/15' },
                  { icon: 'ri-coupon-3-line', title: 'Bono de llantas', desc: 'Única y exclusivamente redimible para tu TECNOMECÁNICA. El valor varía según el rin, marca y precio de la llanta — puede llegar hasta $200.000.', tag: 'Hasta $200.000 para RTM', tagColor: 'text-[#28A745] bg-[#28A745]/10 border-[#28A745]/20' },
                  { icon: 'ri-refresh-line', title: 'Valor 100% redimible', desc: 'Los $50.000 del seguro técnico se aplican directamente en tu revisión tecnomecánica.', tag: null, tagColor: '' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#28A745]/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${feature.icon} text-[#28A745] text-base`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <p className="text-[#1A2B3C] font-semibold text-sm">{feature.title}</p>
                        {feature.tag && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${feature.tagColor}`}>{feature.tag}</span>}
                      </div>
                      <p className="text-[#1A2B3C]/45 text-xs">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={handleScrollToForm} className="w-full py-3 bg-[#28A745] hover:bg-[#218838] text-white font-bold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-car-line text-base"></i>
                Registrar mi vehículo
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div className="text-center" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.4 }}>
          <div className="inline-block bg-[#1A2B3C] rounded-2xl px-8 py-5">
            <p className="text-white/40 text-xs uppercase tracking-widest mb-2 font-bold">Mensaje clave</p>
            <p className="text-white font-black text-xl md:text-2xl tracking-tight">
              "No es un gasto, es una{' '}
              <span className="text-[#28A745]">decisión inteligente</span>"
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
